<template>
    <h3 class="mt-l">Generate a new authentication key pair</h3>
    <p class="mt-xs">Both fields are required. Key duration may not exceed {{ maxDuration_h }} hours.</p>
    <form @submit.prevent="generateKey" v-click-outside="resetAlerts">
        <div id="input-row">
            <div class="input" id="dur-input" :class="{'error' : errors[0] === true}">
                <div class="spaced-row"><label for="duration">Duration (hours)</label><span>*</span></div>
                <input type="text" v-model="duration_h" :disabled="formDisabled" id="duration" aria-required="true" />
            </div>
            <div class="input" id="desc-input" :class="{'error' : errors[1] === true}">  
                <div class="row"><label for="description">Key description</label><span>*</span></div>
                <input type="text" v-model="description" placeholder="E.g. Work laptop,..." :disabled="formDisabled" id="description" aria-required="true" />
            </div>
        </div>
        <AlertBox v-if="alert.msg != ''" 
            :alertMsg="alert.msg"
            :successful="alert.successful"
        />
        <button type="submit" id="generate-button" :class="{'disabled-button' : formDisabled}" :disabled="formDisabled">Generate key</button>
    </form>
    <p v-show="formDisabled" class="p-disabled">You have reached the maximum amount of keys for this project.</p>
</template>

<script>

import axios from "axios";
import AlertBox from './AlertBox.vue';

export default {
    name: 'KeyGeneration',
    props: ['prjnum', 'keyCount'],
    emits: ['key-generated'],
    components: { 
        AlertBox 
    },
    data() {
        return {
            duration_h: "",
            description: "",
            alert: { msg: "", successful: Boolean},
            errors: [false, false]
        }
    },
    methods: {
        checkIfInt(str) {
            let num = Number(str);
            return Number.isInteger(num);
        },
        checkValidity() {
            let duration_s = this.duration_h*3600
            if (this.duration_h === '' || this.description === '') { // empty field(s)

                if (this.duration_h === '' && this.description_h === '') { this.alert = { msg: "Please fill both fields", successful: false }; this.errors = [true, true];}
                else if (this.duration_h === '' ) { this.alert = { msg: "A key duration is required", successful: false }; this.errors = [true, false]; }
                else { this.alert = { msg: "Please write a key description", successful: false }; this.errors = [false, true]; }
            }

            else if (this.minDuration_h === undefined || this.maxDuration_h === undefined || this.maxDescription === undefined) {
                this.alert = { msg: "Error. Try again later", successful: false }; this.errors = [true, true];
            }

            else if (duration_s < this.minDuration_h*3600 || duration_s > this.maxDuration_h*3600) { this.alert = { msg: "Duration has to be between " + this.minDuration_h + "-" + this.maxDuration_h + " hours", successful: false }; this.errors = [true, false]; }
            else if (!this.checkIfInt(duration_s)) { this.alert = { msg: "Duration has to be a whole number", successful: false }; this.errors = [true, false]; }
            else if (!this.description.match(/^[0-9a-z\x20]+$/i)) { this.alert = { msg: "Description must contain only alphanumeric characters and spaces", successful: false }; this.errors = [false, true]; }
            else if (this.description.length > this.maxDescription)  { this.alert = { msg: "Description exceeds " + this.maxDescription + " characters", successful: false }; this.errors = [false, true]; }
            else { this.alert = { msg: '', successful: true}; this.errors = [false, false]; }
        },
        keyGenerated() {
            this.$emit('key-generated');
        },
        generateKey() {

            this.checkValidity();
            
            if (!this.errors[0] && !this.errors[1]) {
                
                let self = this;
                let url = ('/api/v1/project/' + this.prjnum + '/key');

                let data = {
                    "user_note": this.description,
                    "duration": this.duration_h*3600 
                };    
            
                axios.post(url, data)    
                .then((response) => { 
                    console.log(response.statusText);
                    this.duration_h = "";
                    this.description ="";
                    this.keyGenerated();
                    this.alert = {msg: "Key created successfully", successful: true};
                }).catch((error) => {
                    console.log(error.response.data.detail[0]);
                    let msg = error.response.data.detail[0].loc + ": " + error.response.data.detail[0].msg;
                    self.alert = {msg: msg, successful: false};
                }) 
            }     
        },
        resetAlerts() {
            this.alert = { msg: "", successful: Boolean};
        },
        resetAll() {
            this.errors = [false, false];
            this.duration_h = '';
            this.description = '';
            this.resetAlerts();
        },
    },
    computed: {
        maxDuration_h() {
            return this.$store.state.keyLimits.max_key_live_duration/3600;
        },
        minDuration_h() {
            return this.$store.state.keyLimits.min_key_live_duration/3600;
        },
        maxDescription() {
            return this.$store.state.keyLimits.max_description_length;
        },
        formDisabled() {
            return this.keyCount >= this.$store.state.keyLimits.max_key_limit;
        }
    },
    watch: {
        prjnum() { //reset everything when changing between projects
            this.resetAll();
        },
        alert(newVal) { 
            if (newVal.successful === true) {
                setTimeout(() => this.resetAlerts(), 3000);
            }
        }
    }
}

</script>

<style scoped>

.error, .error label {
    color: var(--red);
}

.error input {
    border: 1px solid var(--red);
}

#generate-button {
    margin-top: 1.5rem;
    float: right;
}

.input {
    display: flex;
    flex-direction: column;
}

#dur-input {
   max-width: 30%;
}

#desc-input {
    margin-left: 1.5rem;
    min-width: 50%;
    flex-grow: 1;
}

#input-row, .row {
    display: flex;
    flex-direction: row;
}

.spaced-row {
    justify-content: space-between;
}

#input-row {
    margin-top: 1.5rem;
}

.p-disabled {
    clear: right;
    padding-top: 2rem;
}

@media screen and (max-width: 500px) {
    #dur-input {
        max-width: 40%;
    }
}

</style>